const API_URL = String(import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1');

let accessToken: string | null = null;
let outletId: string | null = null;
let refreshRequest: Promise<boolean> | null = null;

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function setOutletContext(id: string | null) {
  outletId = id;
}

export function clearClientSession() {
  accessToken = null;
  outletId = null;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const body = (await response.json().catch(() => null)) as
    { error?: { message?: string | string[] }; message?: string | string[] } | T | null;
  if (!response.ok) {
    const nestedMessage =
      body && typeof body === 'object' && 'error' in body ? body.error?.message : undefined;
    const legacyMessage =
      body && typeof body === 'object' && 'message' in body ? body.message : undefined;
    const rawMessage = nestedMessage ?? legacyMessage;
    const message = Array.isArray(rawMessage) ? rawMessage.join(', ') : rawMessage;
    throw new ApiError(message || 'Permintaan tidak dapat diproses.', response.status);
  }
  return body as T;
}

async function refreshAccessToken(): Promise<boolean> {
  if (!refreshRequest) {
    refreshRequest = fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
      .then(async (response) => {
        if (!response.ok) return false;
        const session = (await response.json()) as { accessToken: string };
        setAccessToken(session.accessToken);
        return true;
      })
      .finally(() => {
        refreshRequest = null;
      });
  }
  return refreshRequest;
}

export async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
  retry = true,
): Promise<T> {
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(outletId ? { 'X-Outlet-Id': outletId } : {}),
      ...init.headers,
    },
  });

  if (response.status === 401 && retry && !path.startsWith('/auth/')) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return apiRequest<T>(path, init, false);
    clearClientSession();
    window.dispatchEvent(new Event('ajisai:session-expired'));
  }
  return parseResponse<T>(response);
}

export async function apiDownload(
  path: string,
  retry = true,
): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(outletId ? { 'X-Outlet-Id': outletId } : {}),
    },
  });

  if (response.status === 401 && retry) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return apiDownload(path, false);
    clearClientSession();
    window.dispatchEvent(new Event('ajisai:session-expired'));
  }
  if (!response.ok) await parseResponse<never>(response);

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] ?? 'laporan';
  return { blob: await response.blob(), filename };
}

export async function restoreSession() {
  const session = await apiRequest<AuthSession>('/auth/refresh', { method: 'POST' }, false);
  setAccessToken(session.accessToken);
  return session;
}

export type AuthUser = {
  id: string;
  email: string;
  name: string;
  roles: string[];
  permissions: string[];
  outletIds: string[];
  allOutlets: boolean;
};

export type AuthSession = {
  accessToken: string;
  expiresIn: string;
  user: AuthUser;
};
